<template>
  <div id="send-fcm-modal" role="dialog" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <form @submit.prevent="sendMessage">
        <div class="modal-header">
          <h5 class="modal-title">
            <template v-if="isBulkMode">Send message to {{ props.driverIds!.length }} drivers</template>
            <template v-else>{{ driver ? $t('drivers.actions.send_message_to', { name: driver.name }) : $t('drivers.actions.send_message_to_all') }}</template>
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="title" class="form-label">{{ $t('common.fields.title') }}</label>
            <input id="title" class="form-control" v-model="message.title" required />
          </div>
          <div class="mb-3">
            <label for="message" class="form-label">{{ $t('common.fields.message') }}</label>
            <textarea id="message" class="form-control" v-model="message.body" required rows="3"></textarea>
          </div>
          <div class="mb-3">
            <label for="duration" class="form-label">{{ $t('common.placeholders.duration') }}</label>
            <input id="duration" class="form-control w-25" v-model="duration" required type="number"/>
          </div>
          <div v-if="error" class="alert alert-danger">{{ error }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger" @click="close">{{ $t('common.actions.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ $t('common.actions.submit') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue'
import FcmService from '@/services/FcmService'
import DriverRepository from '@/repositories/DriverRepository'
import { useI18n } from 'vue-i18n'
import ToastService from '@/services/ToastService'
import { Modal } from 'bootstrap'
import { DriverInterface } from '@/types/DriverInterface'
import { FCMNotification } from '@/types/FCMNotifications'
import DateHelper from '@/helpers/DateHelper'

const props = defineProps<{
  driver?: DriverInterface | null
  driverIds?: string[]
}>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'sent'): void
}>()

const isBulkMode = computed(() => Array.isArray(props.driverIds) && props.driverIds.length > 0)
const error = ref<string | null>(null)
const fcmModal = ref<Modal | null>(null)
const { t } = useI18n()
const duration = ref<string>('15')
const message = ref<FCMNotification>({
  title: '',
  body: '',
  data: {
    duration: duration.value.toString(),
  }
})
const submitting = ref(false)

function close(): void {
  message.value = {
    title: '',
    body: '',
    data: {
      duration: duration.value.toString(),
    }
  }
  error.value = null
  emit('close')
}

watch(() => duration.value, (newDuration) => {
  if (newDuration === '' || isNaN(Number(newDuration)) || Number(newDuration) <= 0) {
    error.value = t('common.errors.invalid_duration')
    newDuration = '15'
  } else {
    error.value = null
  }
  message.value.data = {
    duration: newDuration.toString()
  }
})

async function sendMessage(): Promise<void> {
  submitting.value = true
  message.value.data = {
    duration: duration.value.toString(),
    timestamp: (DateHelper.unix() * 1000).toString()
  }
  if (isBulkMode.value) {
    DriverRepository.bulkSendMessage({
      driverIds: props.driverIds!,
      title: message.value.title,
      body: message.value.body,
      data: { duration: duration.value.toString() },
    }).then(() => {
      ToastService.toast(ToastService.SUCCESS, t('drivers.actions.sent_success'))
    }).catch((err) => {
      ToastService.toast(ToastService.ERROR, err.message)
      error.value = err.message
    }).finally(() => {
      close()
      submitting.value = false
    })
  } else if (props.driver) {
    FcmService.sendToDriver(props.driver.id, message.value).then(() => {
      ToastService.toast(ToastService.SUCCESS, t('drivers.actions.sent_success'))
    }).catch((err) => {
      ToastService.toast(ToastService.ERROR, err.message)
      error.value = err.message
    }).finally(() => {
      close()
      submitting.value = false
    })
  } else {
    FcmService.sendToAllDrivers(message.value).then(() => {
      ToastService.toast(ToastService.SUCCESS, t('drivers.actions.sent_success'))
    }).catch((err) => {
      ToastService.toast(ToastService.ERROR, err.message)
      error.value = err.message
    }).finally(() => {
      close()
      submitting.value = false
    })
  }
}
</script>